/**
 * IndexedDB Mail Service for CLOU Portal
 * Provides high-performance, user-isolated, quota-free client caching for emails.
 * Enables 0ms instant mailbox rendering on login/refresh with background sync.
 */

const DB_NAME = 'clou_mail_db';
const DB_VERSION = 1;
const STORE_NAME = 'emails';

/**
 * Open or upgrade the IndexedDB database
 */
const openDb = () => {
  return new Promise((resolve, reject) => {
    if (typeof window === 'undefined' || !window.indexedDB) {
      return reject(new Error('IndexedDB is not supported in this environment'));
    }

    const request = window.indexedDB.open(DB_NAME, DB_VERSION);

    request.onupgradeneeded = (event) => {
      const db = event.target.result;
      if (!db.objectStoreNames.contains(STORE_NAME)) {
        const store = db.createObjectStore(STORE_NAME, { keyPath: 'compositeKey' });
        store.createIndex('userEmail', 'userEmail', { unique: false });
        store.createIndex('timestamp', 'timestamp', { unique: false });
        store.createIndex('folder', 'folder', { unique: false });
      }
    };

    request.onsuccess = (event) => {
      resolve(event.target.result);
    };

    request.onerror = (event) => {
      console.warn('[IndexedDB] Database open error:', event.target.error);
      reject(event.target.error);
    };
  });
};

/**
 * Normalizes user email to lowercase
 */
const normalizeEmail = (email) => (email || '').trim().toLowerCase();

/**
 * Retrieve all cached emails for a specific user from IndexedDB
 * @param {string} userEmail
 * @returns {Promise<Array>} List of emails sorted by timestamp descending
 */
export const getUserEmailsFromDb = async (userEmail) => {
  const cleanEmail = normalizeEmail(userEmail);
  if (!cleanEmail) return [];

  try {
    const db = await openDb();
    return new Promise((resolve) => {
      const tx = db.transaction(STORE_NAME, 'readonly');
      const store = tx.objectStore(STORE_NAME);
      const index = store.index('userEmail');
      const request = index.getAll(IDBKeyRange.only(cleanEmail));

      request.onsuccess = () => {
        const results = request.result || [];
        // Extract original email payload and sort by timestamp descending
        const emailList = results.map(item => item.emailData || item);
        emailList.sort((a, b) => {
          const timeA = new Date(a.Timestamp || a.DeliveredAt || 0).getTime();
          const timeB = new Date(b.Timestamp || b.DeliveredAt || 0).getTime();
          return timeB - timeA;
        });
        resolve(emailList);
      };

      request.onerror = (err) => {
        console.warn('[IndexedDB] Read emails error:', err);
        resolve([]);
      };
    });
  } catch (e) {
    console.warn('[IndexedDB] getUserEmailsFromDb failed:', e);
    return [];
  }
};

/**
 * Save / Upsert a list of emails for a specific user into IndexedDB
 * @param {string} userEmail
 * @param {Array} emailList
 * @returns {Promise<boolean>}
 */
export const saveUserEmailsToDb = async (userEmail, emailList) => {
  const cleanEmail = normalizeEmail(userEmail);
  if (!cleanEmail || !Array.isArray(emailList) || emailList.length === 0) {
    return false;
  }

  try {
    const db = await openDb();
    return new Promise((resolve) => {
      const tx = db.transaction(STORE_NAME, 'readwrite');
      const store = tx.objectStore(STORE_NAME);

      tx.oncomplete = () => {
        resolve(true);
      };

      tx.onerror = (err) => {
        console.warn('[IndexedDB] Save transaction error:', err);
        resolve(false);
      };

      for (const email of emailList) {
        if (!email || !email.EmailID) continue;
        const record = {
          compositeKey: `${cleanEmail}#${email.EmailID}`,
          userEmail: cleanEmail,
          emailId: email.EmailID,
          folder: email.Folder || (email.EmailID.includes('-inbox') ? 'inbox' : 'sent'),
          timestamp: email.Timestamp || email.DeliveredAt || new Date().toISOString(),
          emailData: email
        };
        store.put(record);
      }
    });
  } catch (e) {
    console.warn('[IndexedDB] saveUserEmailsToDb failed:', e);
    return false;
  }
};

/**
 * Delete specific emails from IndexedDB for a user
 * @param {string} userEmail
 * @param {Array<string>} emailIds
 * @returns {Promise<boolean>}
 */
export const deleteUserEmailsFromDb = async (userEmail, emailIds) => {
  const cleanEmail = normalizeEmail(userEmail);
  if (!cleanEmail || !Array.isArray(emailIds) || emailIds.length === 0) {
    return false;
  }

  try {
    const db = await openDb();
    return new Promise((resolve) => {
      const tx = db.transaction(STORE_NAME, 'readwrite');
      const store = tx.objectStore(STORE_NAME);

      tx.oncomplete = () => resolve(true);
      tx.onerror = () => resolve(false);

      for (const id of emailIds) {
        if (!id) continue;
        store.delete(`${cleanEmail}#${id}`);
      }
    });
  } catch (e) {
    console.warn('[IndexedDB] deleteUserEmailsFromDb failed:', e);
    return false;
  }
};

/**
 * Clear all cached emails for a specific user
 * @param {string} userEmail
 * @returns {Promise<boolean>}
 */
export const clearUserEmailsFromDb = async (userEmail) => {
  const cleanEmail = normalizeEmail(userEmail);
  if (!cleanEmail) return false;

  try {
    const db = await openDb();
    return new Promise((resolve) => {
      const tx = db.transaction(STORE_NAME, 'readwrite');
      const store = tx.objectStore(STORE_NAME);
      const index = store.index('userEmail');
      const request = index.openCursor(IDBKeyRange.only(cleanEmail));

      request.onsuccess = (event) => {
        const cursor = event.target.result;
        if (cursor) {
          cursor.delete();
          cursor.continue();
        } else {
          resolve(true);
        }
      };

      request.onerror = () => resolve(false);
    });
  } catch (e) {
    console.warn('[IndexedDB] clearUserEmailsFromDb failed:', e);
    return false;
  }
};
